import {
  type ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltipContent,
  ChartTooltip
} from '@ui/chart.tsx';
import { Pie, PieChart } from 'recharts';
import type { InsightCompanyInsightCompanyReadModel } from '@r/api/generated/client.schemas.ts';

type CompanyInsights = InsightCompanyInsightCompanyReadModel;

export type CompaniesByStepsRenderingData = {
  id: string;
  stepName: string;
  totalCompanies: number;
  fill: string;
};

export const processCompaniesByStepsResults = (
  totalCompaniesByStatus: number,
  totalCompaniesPerStep: CompanyInsights['companiesPerStep'],
  companyNoStep: string
): CompaniesByStepsRenderingData[] | [] => {
  // Initialize the total number of companies who have completed steps
  let companiesCount = 0;

  const totalCompaniesByStepsForRendering = totalCompaniesPerStep?.map((item) => {
    companiesCount += item.totalCompanies;

    return {
      id: `${item.id}`,
      stepName: item.name,
      totalCompanies: item.totalCompanies,
      fill: `var(--color-${item.id})`
    };
  });

  // Calculate the total number of companies who have not completed any steps
  const totalCompaniesWithoutStep = totalCompaniesByStatus - companiesCount;

  // If there are companies who have not completed any steps, add them to the list
  if (totalCompaniesWithoutStep) {
    totalCompaniesByStepsForRendering?.unshift({
      id: '0',
      stepName: `${companyNoStep}`,
      totalCompanies: totalCompaniesWithoutStep,
      fill: 'var(--color-0)'
    });
  }

  return totalCompaniesByStepsForRendering || [];
};

function createChartConfig(chartData: CompaniesByStepsRenderingData[]): ChartConfig {
  // Generate a simple gradient of the primary colors
  const colors = chartData.map((_, index) => {
    const opacity = 1 - index / chartData.length;
    return `hsl(var(--primary) / ${opacity})`;
  });

  return chartData.reduce((config, item, index) => {
    config[item.id] = {
      label: item.stepName,
      color: colors[index]
    };
    return config;
  }, {} as ChartConfig);
}

export const CompaniesByStepsPieChartBloc = ({
  chartData
}: {
  chartData: CompaniesByStepsRenderingData[];
}) => {
  return (
    <ChartContainer config={createChartConfig(chartData)} className="h-[350px] w-full">
      <PieChart>
        <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel />} />

        <Pie
          label
          labelLine={false}
          data={chartData}
          dataKey="totalCompanies"
          nameKey="id"
          stroke={'hsl(var(--background))'}
          strokeWidth={1.5}
        />

        <ChartLegend
          content={<ChartLegendContent nameKey="id" />}
          className="-translate-y-2 flex-wrap gap-2 [&>*]:basis-1/4 [&>*]:justify-center"
        />
      </PieChart>
    </ChartContainer>
  );
};
